import { memo, useContext, useMemo } from 'react'
import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'

import { DataTableContext } from './data-table-context'

import type { DataTableModel } from '../atoms'
import type { Column, ColumnJustify } from './data-table-types'
import type { ClassNameProps, StyleProps } from '@root/shared/react'

export type DataTableRowProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  rowData: D | undefined
  index: number
  columns: ReadonlyArray<Column<D, ColumnId, Context>>
  rowHeight: number
  getRowStyle?: (row: D) => React.CSSProperties
  model: DataTableModel<D, ColumnId, object, Context>
  context: Context
  renderBackgroundLink?: (row: { data: D; index: number }) => React.ReactNode
  onRowClick?: (value: D, e: React.MouseEvent<HTMLDivElement>) => void
  style?: React.CSSProperties
} & ClassNameProps &
  StyleProps

const DataTableRow_ = memo(
  ({
    rowData,
    index,
    columns,
    rowHeight = 30,
    getRowStyle,
    model,
    context,
    onRowClick,
    style,
    renderBackgroundLink,
  }: DataTableRowProps<object, string, object | undefined>) => {
    type ResolvedColumn = Column<object, string, object | undefined>

    const resolvedStyle = {
      height: rowHeight,
      ...style,
    }

    const { disableIndex } = useContext(DataTableContext)

    // We cannot rely on css even/odd selector because of the virtualized list
    const isEven = index % 2 === 0
    const visibleColumns = useMemo(
      () => columns.filter((column) => !column.hidden),
      [columns],
    )

    if (!rowData) {
      const renderSkeletonCell = (column: ResolvedColumn) => {
        if (column.hidden) {
          return undefined
        }

        return (
          <SkeletonCell key={column.id} $justify={column.justify ?? 'start'}>
            <Skeleton height={'100%'} width={'100%'} variant="rectangle" />
          </SkeletonCell>
        )
      }

      return (
        <Row key={'skeleton'} style={resolvedStyle}>
          {visibleColumns.map(renderSkeletonCell)}
        </Row>
      )
    }

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.target as Node)) {
        return
      }

      onRowClick?.(rowData, e)
    }

    return (
      <Row
        data-even={isEven}
        data-clickable={!!onRowClick}
        style={{ ...resolvedStyle, ...getRowStyle?.(rowData) }}
        onClick={handleClick}
      >
        {renderBackgroundLink
          ? renderBackgroundLink({ data: rowData, index })
          : undefined}
        <RowCells
          columns={visibleColumns}
          rowData={rowData}
          model={model}
          context={context}
          disablePointerEvents={!!renderBackgroundLink}
          index={disableIndex ? 0 : index}
        />
      </Row>
    )
  },
)

export const DataTableRow = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>(
  props: DataTableRowProps<D, ColumnId, Context>,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <DataTableRow_ {...(props as any)} />
}

const Row = styled.div`
  display: grid;
  align-items: center;
  position: relative;
  padding-inline: var(--data-table-row-padding-inline);

  background: var(--data-table-row-odd-bg);
  &[data-even='true'] {
    background: var(--data-table-row-even-bg);
  }

  border-bottom: 1px solid var(--data-table-row-border-color);

  &[data-clickable='true'] {
    cursor: pointer;
    transition: background 0.2s ease-in-out;
    &:hover {
      background: var(--backgroundA-3);
    }
  }

  &:hover {
    background: var(--backgroundA-2);
  }
`

type RowCellsProps = Pick<
  DataTableRowProps<object, string, object | undefined>,
  'columns' | 'rowData' | 'model' | 'context' | 'index'
>

const RowCells = memo(
  ({
    columns,
    rowData,
    disablePointerEvents,
    model,
    context,
    index,
  }: RowCellsProps & { disablePointerEvents: boolean }) => {
    const renderCell = (
      column: Column<object, string, object | undefined>,
      data: object | undefined,
    ) => {
      if (column.hidden || !data) {
        return undefined
      }

      return (
        <Cell
          key={column.id}
          $justify={column.justify ?? 'start'}
          $disablePointerEvents={disablePointerEvents}
          css={column.css}
          style={{ minWidth: column.minWidth, maxWidth: column.maxWidth }}
        >
          {column.render({
            value: data,
            model,
            column,
            context,
            index,
          })}
        </Cell>
      )
    }

    return <>{columns.map((column) => renderCell(column, rowData))}</>
  },
)

const Cell = styled.div<{
  $justify?: ColumnJustify
  $disablePointerEvents: boolean
}>`
  pointer-events: ${({ $disablePointerEvents }) =>
    $disablePointerEvents ? 'none' : 'auto'};
  overflow: hidden;
  max-width: 100%;
  display: grid;
  z-index: 0;
  min-width: 0;
  justify-content: ${({ $justify }) => $justify};
`
const SkeletonCell = styled.div<{ $justify?: ColumnJustify }>`
  overflow: hidden;
  display: flex;
  padding-block: 10px;
  padding-inline: 0 10px;
  width: 100%;
  height: 100%;
  justify-content: ${({ $justify }) => $justify};
`
